/**
 * VELUNE — comportements des blocs et sections sur mesure.
 * Un seul fichier, chargé une fois sur toutes les pages (snippet velune-head).
 * Les textes et réglages viennent toujours du HTML (attributs data-*), jamais d'ici.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const define = (name, cls) => {
  if (!customElements.get(name)) customElements.define(name, cls);
};

/* ------------------------------------------------------------------ */
/* Ajout au panier partagé (suggestions panier, ventes croisées)       */
/* ------------------------------------------------------------------ */

/**
 * Ajoute des articles au panier et prévient le thème Horizon (tiroir, icône, page panier).
 * @param {{id: number|string, quantity: number}[]} items
 * @param {Element} source - élément qui déclenche l'ajout (reçoit les événements)
 */
export async function addToCart(items, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity })),
        sections: sectionIds.join(','),
      }),
    }).then((r) => r.json());

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );

    const failed = Boolean(added.status);
    if (failed) {
      source.dispatchEvent(
        new CartErrorEvent({ error: added.description || added.message || 'Add to cart failed', code: 'INVALID' })
      );
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: failed ? undefined : added.sections,
        source: 'velune',
        didError: failed,
      },
    });
    return !failed;
  } catch (error) {
    console.error('[velune] ajout au panier impossible', error);
    deferred.reject(error);
    return false;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-vl-add]') : null;
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();

  button.setAttribute('aria-busy', 'true');
  const ok = await addToCart([{ id: button.dataset.vlAdd, quantity: Number(button.dataset.vlQty) || 1 }], button);
  button.removeAttribute('aria-busy');

  // Le panier se re-rend : le bouton peut avoir disparu. Sinon, retour visuel.
  if (ok && button.isConnected && button.dataset.vlAdded) {
    const label = button.querySelector('span');
    const original = label?.textContent;
    button.classList.add('is-added');
    if (label) label.textContent = button.dataset.vlAdded;
    setTimeout(() => {
      button.classList.remove('is-added');
      if (label && original) label.textContent = original;
    }, 2000);
  }
});

/* ------------------------------------------------------------------ */
/* Offres par quantité : règle la quantité du formulaire produit       */
/* ------------------------------------------------------------------ */
define(
  'vl-bundles',
  class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.#onChange);
      customElements.whenDefined('quantity-selector-component').then(() => {
        const checked = this.querySelector('input:checked');
        if (checked) this.#apply(Number(checked.value));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.#onChange);
    }

    #onChange = (event) => {
      if (event.target instanceof HTMLInputElement) this.#apply(Number(event.target.value));
    };

    #apply(quantity) {
      const qty = quantity > 0 ? quantity : 1;
      const scope = this.closest('.shopify-section') || document;
      scope.querySelectorAll('product-form-component quantity-selector-component').forEach((selector) => {
        if (typeof selector.setValue === 'function') selector.setValue(String(qty));
        else {
          const input = selector.querySelector('input');
          if (input) input.value = String(qty);
        }
        selector.dispatchEvent(new QuantitySelectorUpdateEvent(qty));
      });
    }
  }
);

/* ------------------------------------------------------------------ */
/* Outils de date                                                       */
/* ------------------------------------------------------------------ */

/** Décalage (ms) du fuseau `tz` par rapport à UTC à l'instant `date`. */
function tzOffset(date, tz) {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
        .formatToParts(date)
        .map((p) => [p.type, p.value])
    );
    const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    return asUtc - Math.floor(date.getTime() / 1000) * 1000;
  } catch {
    return -date.getTimezoneOffset() * 60000;
  }
}

/** Date « murale » du fuseau, exprimée avec les champs UTC d'un objet Date. */
const wallClock = (now, tz) => new Date(now.getTime() + tzOffset(now, tz));
const isWeekend = (d) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

/** Prochain instant (timestamp) où il est `hour`:00 dans le fuseau, en sautant le week-end si demandé. */
function nextCutoff(now, tz, hour, skipWeekend) {
  const off = tzOffset(now, tz);
  const wall = wallClock(now, tz);
  for (let add = 0; add < 8; add++) {
    const d = new Date(Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate() + add, hour, 0, 0));
    if (skipWeekend && isWeekend(d)) continue;
    const t = d.getTime() - off;
    if (t > now.getTime()) return t;
  }
  return NaN;
}

/** Interprète « AAAA-MM-JJ » ou « AAAA-MM-JJ HH:MM » comme heure locale du fuseau. */
function parseWallDate(value, tz) {
  const m = String(value || '')
    .trim()
    .match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (!m) return NaN;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 23, m[5] ? +m[5] : 59, m[4] ? 0 : 59);
  return wall - tzOffset(new Date(wall), tz);
}

const fill = (template, values) => template.replace(/\[(\w+)\]/g, (all, key) => (key in values ? values[key] : all));

/* ------------------------------------------------------------------ */
/* Compte à rebours (bloc produit)                                     */
/* ------------------------------------------------------------------ */
define(
  'vl-timer',
  class extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-time]');
      this.#tick();
      this.timer = setInterval(() => this.#tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    #target(now) {
      const { mode, tz = 'Europe/Paris', end, hour, weekdays } = this.dataset;
      if (mode === 'date') return parseWallDate(end, tz);
      return nextCutoff(now, tz, Number(hour) || 14, weekdays === 'true');
    }

    #tick() {
      const now = new Date();
      const target = this.#target(now);
      if (!(target > now.getTime())) {
        this.hidden = true;
        clearInterval(this.timer);
        return;
      }
      let s = Math.floor((target - now.getTime()) / 1000);
      const d = Math.floor(s / 86400);
      s %= 86400;
      const h = Math.floor(s / 3600);
      s %= 3600;
      const m = Math.floor(s / 60);
      s %= 60;
      const pad = (n) => String(n).padStart(2, '0');
      const { labelDays = 'j', labelHours = 'h', labelMinutes = 'min', labelSeconds = 's' } = this.dataset;
      let text = `${pad(h)} ${labelHours} ${pad(m)} ${labelMinutes}`;
      text = d > 0 ? `${d} ${labelDays} ${text}` : `${text} ${pad(s)} ${labelSeconds}`;
      if (this.output) this.output.textContent = text;
      this.hidden = false;
    }
  }
);

/* ------------------------------------------------------------------ */
/* Livraison estimée                                                    */
/* ------------------------------------------------------------------ */
define(
  'vl-delivery',
  class extends HTMLElement {
    connectedCallback() {
      const { tz = 'Europe/Paris', min = '3', max = '7', cutoff = '14', prep = '1', weekdays, locale } = this.dataset;
      const skip = weekdays === 'true';
      const now = new Date();
      const wall = wallClock(now, tz);

      // Jour d'expédition : aujourd'hui si avant l'heure limite (et jour ouvré), sinon jour ouvré suivant,
      // puis + délai de préparation.
      let ship = new Date(Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate()));
      const afterCutoff = wall.getUTCHours() >= Number(cutoff);
      const addDays = (date, days) => {
        const d = new Date(date);
        let left = days;
        while (left > 0) {
          d.setUTCDate(d.getUTCDate() + 1);
          if (!(skip && isWeekend(d))) left--;
        }
        return d;
      };
      if (skip && isWeekend(ship)) ship = addDays(ship, 1);
      else if (afterCutoff) ship = addDays(ship, 1);
      ship = addDays(ship, Math.max(0, Number(prep) - 1));

      const from = addDays(ship, Number(min));
      const to = addDays(ship, Number(max));
      const fmt = new Intl.DateTimeFormat(locale || document.documentElement.lang || 'fr', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      });
      const output = this.querySelector('[data-text]');
      if (output) {
        output.innerHTML = fill(this.dataset.template || '', {
          debut: `<strong class="vl-note__strong">${fmt.format(from)}</strong>`,
          fin: `<strong class="vl-note__strong">${fmt.format(to)}</strong>`,
        });
      }
      this.hidden = false;
    }
  }
);

/* ------------------------------------------------------------------ */
/* Copier un code promo                                                 */
/* ------------------------------------------------------------------ */
define(
  'vl-copy',
  class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
    }

    #onClick = async (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button) return;
      const label = button.querySelector('span') || button;
      const original = label.textContent;
      try {
        await navigator.clipboard.writeText(this.dataset.code || '');
        label.textContent = this.dataset.copied || original;
      } catch {
        label.textContent = this.dataset.code || original;
      }
      setTimeout(() => (label.textContent = original), 2000);
    };
  }
);

/* ------------------------------------------------------------------ */
/* « Vu dans » : citation liée au logo sélectionné                      */
/* ------------------------------------------------------------------ */
define(
  'vl-press',
  class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      const autoplay = Number(this.dataset.autoplay) || 0;
      if (autoplay > 0) {
        this.interval = setInterval(() => {
          if (this.matches(':hover')) return;
          const tabs = [...this.querySelectorAll('[data-quote-index]')];
          const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
          tabs[(current + 1) % tabs.length]?.click();
        }, autoplay * 1000);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      clearInterval(this.interval);
    }

    #onClick = (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[data-quote-index]') : null;
      if (!tab) return;
      const index = tab.dataset.quoteIndex;
      this.querySelectorAll('[data-quote-index]').forEach((t) =>
        t.setAttribute('aria-selected', String(t.dataset.quoteIndex === index))
      );
      this.querySelectorAll('[data-quote]').forEach((q) => (q.hidden = q.dataset.quote !== index));
    };
  }
);

/* ------------------------------------------------------------------ */
/* Carrousel horizontal (flèches)                                      */
/* ------------------------------------------------------------------ */
define(
  'vl-rail',
  class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
    }

    #onClick = (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-dir]') : null;
      const track = this.querySelector('.vl-rail');
      if (!button || !track) return;
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: step * Number(button.dataset.dir), behavior: 'smooth' });
    };
  }
);

/* ------------------------------------------------------------------ */
/* Vidéo cliente : lecture auto en sourdine quand visible, son au clic  */
/* ------------------------------------------------------------------ */
define(
  'vl-video',
  class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;
      if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? this.video.play().catch(() => {}) : this.video.pause()),
          { threshold: 0.5 }
        );
        this.observer.observe(this);
      }
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.#onClick);
    }

    #onClick = (event) => {
      if (!this.video || (event.target instanceof Element && event.target.closest('a'))) return;
      const sound = this.querySelector('[data-sound]');
      if (this.video.paused) {
        this.video.muted = false;
        this.video.play().catch(() => {});
      } else if (event.target instanceof Element && event.target.closest('[data-sound]')) {
        this.video.muted = !this.video.muted;
      } else {
        this.video.pause();
      }
      sound?.setAttribute('aria-pressed', String(!this.video.muted));
    };
  }
);
